import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ChevronDown, Search } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import LanguageSelector from "./LanguageSelector";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [openMobileDropdown, setOpenMobileDropdown] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const isActive = (path) => location.pathname === path;

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
    }
  };

  const navLinks = [
    { path: "/", label: t('home') },
    { 
      label: t('history'), 
      dropdown: [
        { path: "/histoire/origines", label: t('origins') },
        { path: "/histoire/maodo", label: t('maodo') },
        { path: "/histoire/khalifes", label: t('heirsLineage') },
        { path: "/arbre-genealogique", label: t('familyTree') },
        { path: "/histoire/geographie", label: t('sacredGeography') },
        { path: "/carte", label: t('interactiveMap') }
      ]
    },
    { 
      label: t('teachings'),
      dropdown: [
        { path: "/enseignements/piliers", label: t('pillars') },
        { path: "/enseignements/ecole", label: t('school') },
        { path: "/enseignements/ouvrages", label: t('referenceWorks') }
      ]
    },
    { path: "/wattu", label: "Wattu" },
    { 
      label: t('events'),
      dropdown: [
        { path: "/evenements/gamou", label: t('gamou') },
        { path: "/evenements/ziarra", label: t('annualZiarra') },
        { path: "/evenements/ceremonies", label: t('religiousCeremonies') }
      ]
    },
    { path: "/archives", label: t('archives') },
    { path: "/mediatheque", label: t('media') },
    { path: "/contact", label: t('contact') }
  ];

  return (
    <>
      {/* Bismillah Banner */}
      <div className="bg-gradient-to-r from-[#006B47] to-[#004D33] text-white py-2 text-center border-b-2 border-[#E6B800]">
        <p className="bismillah-text text-lg md:text-xl" data-testid="bismillah-banner">
          بِسْمِ اللهِ الرَّحْمٰنِ الرَّحِيْمِ
        </p>
      </div>

      {/* Main Navbar */}
      <nav className="bg-white shadow-lg sticky top-0 z-50" data-testid="main-navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center space-x-3"
              data-testid="logo-link"
            >
              <img 
                src="/logo-vf.png" 
                alt="L'empreinte de Maodo" 
                className="h-16 w-16 rounded-full object-cover shadow-lg border-2 border-[#E6B800] glow-gold"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-6">
              {navLinks.map((link, index) => (
                link.dropdown ? (
                  <div 
                    key={index}
                    className="relative group"
                  >
                    <button
                      className="flex items-center gap-1 text-base font-medium text-[#4A4A4A] hover:text-[#006B47] py-4 transition-colors"
                      data-testid={`nav-dropdown-${link.label.toLowerCase()}`}
                    >
                      {link.label}
                      <ChevronDown className="w-4 h-4 transition-transform duration-200 group-hover:rotate-180" />
                    </button>
                    
                    {/* Dropdown menu - visible on group hover */}
                    <div 
                      className="absolute top-full left-0 w-56 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200"
                    >
                      {/* Invisible bridge to prevent gap */}
                      <div className="h-2"></div>
                      <div className="bg-white rounded-lg shadow-xl border border-gray-100 py-2">
                        {link.dropdown.map((subLink) => (
                          <Link
                            key={subLink.path}
                            to={subLink.path}
                            className="block px-4 py-3 text-sm text-[#4A4A4A] hover:bg-[#E0F2E0] hover:text-[#006B47] transition-colors"
                            data-testid={`nav-sublink-${subLink.label.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            {subLink.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`text-base font-medium transition-colors duration-200 ${
                      isActive(link.path)
                        ? "text-[#006B47] border-b-2 border-[#E6B800]"
                        : "text-[#4A4A4A] hover:text-[#006B47]"
                    } pb-1`}
                  >
                    {link.label}
                  </Link>
                )
              ))}

              {/* Search Bar Desktop */}
              <form onSubmit={handleSearch} className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('search')}
                  data-testid="search-input-desktop"
                  className="w-48 px-4 py-2 pl-10 border border-gray-300 rounded-full focus:outline-none focus:border-[#006B47] focus:ring-1 focus:ring-[#006B47] text-sm"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888888]" />
              </form>

              {/* Language Selector */}
              <LanguageSelector variant="minimal" />
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-[#E0F2E0] transition-colors"
              data-testid="mobile-menu-button"
              aria-label="Toggle menu"
            >
              {isOpen ? (
                <X className="h-6 w-6 text-[#006B47]" />
              ) : (
                <Menu className="h-6 w-6 text-[#006B47]" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div 
            className="lg:hidden bg-white border-t border-gray-200 max-h-[80vh] overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-4 space-y-2">
              {/* Mobile Search */}
              <form onSubmit={handleSearch} className="relative mb-4">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('search')}
                  data-testid="search-input-mobile"
                  className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:border-[#006B47] focus:ring-1 focus:ring-[#006B47]"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#888888]" />
              </form>

              {/* Mobile Language Selector */}
              <div className="mb-4 px-4">
                <LanguageSelector variant="default" />
              </div>

              {navLinks.map((link, index) => (
                link.dropdown ? (
                  <div key={index}>
                    <button
                      onClick={() => setOpenMobileDropdown(openMobileDropdown === link.label ? null : link.label)}
                      className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-base font-medium text-[#4A4A4A] hover:bg-[#E0F2E0] hover:text-[#006B47] transition-colors"
                    >
                      {link.label}
                      <ChevronDown className={`w-4 h-4 transition-transform ${openMobileDropdown === link.label ? 'rotate-180' : ''}`} />
                    </button>
                    {openMobileDropdown === link.label && (
                      <div className="ml-4 mt-1 space-y-1">
                        {link.dropdown.map((subLink) => (
                          <Link
                            key={subLink.path}
                            to={subLink.path}
                            onClick={() => setIsOpen(false)}
                            className="block px-4 py-2 rounded-lg text-sm text-[#4A4A4A] hover:bg-[#E0F2E0] hover:text-[#006B47] transition-colors"
                          >
                            {subLink.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                      isActive(link.path)
                        ? "bg-[#006B47] text-white"
                        : "text-[#4A4A4A] hover:bg-[#E0F2E0] hover:text-[#006B47]"
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              ))}
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;
